import { announcedLanguages, resolveLanguage } from "../i18n/languages";

/**
 * Product announcements shown in the "Ny funksjon" dialog.
 *
 * Each user sees each announcement once. Closing or answering it stores a read
 * receipt on the server (`/feature_announcement_acknowledgements`), so it does
 * not come back on any device. A new update = a new entry with a new `key`.
 *
 * Generic entry (add newest first, all text through t() in no/en/sv/pl):
 * {
 *   key: "feature:2026-10-offer-pdf",        // stable, lowercase, [a-z0-9._:-], never reused
 *   publishedAt: "2026-10-20",               // ISO date; older than MAX_AGE_DAYS is not shown
 *   titleKey: "featureAnnouncements.items.offer_pdf.title",
 *   bodyKey: "featureAnnouncements.items.offer_pdf.body",
 *   highlightKeys: ["…point_1", "…point_2"],  // optional bullet points
 *   learnMorePath: "/dashboard/offers",       // optional in-app link
 *   isRelevant: ({ user }) => true,           // optional audience filter
 * }
 *
 * New languages are announced automatically from `SUPPORTED_LANGUAGES`
 * (`announce: true` + `addedAt`) — no entry needed here.
 */
export const FEATURE_ANNOUNCEMENTS = [];

/** Announcements older than this are not shown to users who never saw them. */
export const MAX_AGE_DAYS = 120;

const LANGUAGE_KEY_PREFIX = "language-added:";

export const languageAnnouncementKey = (code) => `${LANGUAGE_KEY_PREFIX}${code}`;

function languageAnnouncements() {
  return announcedLanguages().map((language) => ({
    key: languageAnnouncementKey(language.code),
    type: "language",
    publishedAt: language.addedAt,
    language,
  }));
}

function isFresh(publishedAt, now) {
  const published = Date.parse(`${publishedAt}T00:00:00Z`);
  if (!Number.isFinite(published)) return false;
  return now - published <= MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Announcements the user still has to see, oldest first (so a queue reads in order).
 * A language announcement is skipped when the user already uses that language
 * (see `alreadyAnsweredByLanguage`, which the host acknowledges silently).
 */
export function pendingAnnouncements({
  acknowledgedKeys,
  currentLanguage,
  user,
  now = Date.now(),
  announcements = FEATURE_ANNOUNCEMENTS,
}) {
  const seen = acknowledgedKeys instanceof Set ? acknowledgedKeys : new Set(acknowledgedKeys || []);
  const current = resolveLanguage(currentLanguage).code;
  return [...announcements.map((item) => ({ type: "feature", ...item })), ...languageAnnouncements()]
    .filter((item) => !seen.has(item.key))
    .filter((item) => isFresh(item.publishedAt, now))
    .filter((item) => (item.type === "language" ? item.language.code !== current : true))
    .filter((item) => (typeof item.isRelevant === "function" ? item.isRelevant({ user }) : true))
    .sort((a, b) => a.publishedAt.localeCompare(b.publishedAt));
}

/** Unseen language announcements for the language the user already uses. */
export function alreadyAnsweredByLanguage({ acknowledgedKeys, currentLanguage }) {
  const seen = acknowledgedKeys instanceof Set ? acknowledgedKeys : new Set(acknowledgedKeys || []);
  const current = resolveLanguage(currentLanguage).code;
  return languageAnnouncements().filter((item) => item.language.code === current && !seen.has(item.key));
}
